import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  useTheme,
  TablePagination,
  Checkbox
} from "@mui/material"
import { Launch as LaunchIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material"
import { useLogger, useSnackbar, useGlobalLoader } from "src/providers"
import { snackbarErrorHandler } from "src/utils/errorHandler"
import { RequestError } from "src/models/errors"
import { styled } from "@mui/material/styles"
import UploadSourcesModal from "./UploadSourcesModal"
import DataSourcesFilter from "./DataSourcesFilter"
import DataSourcesSelectionBar from "./DataSourcesSelectionBar"
import { StatusChip } from "../../components/table/StatusChip"
import SortableTableHeaderCell, { Order } from "../../components/table/SortableTableHeaderCell"
import TableHeaderText from "../../components/table/TableHeaderText"
import { useInterval } from "src/utils/useInterval"
import axios from "axios"
import moment from "moment"
import { useResponsive } from "src/utils/useResponsive"
import { useSearchQuery } from "./useSearchQuery"
import { useStatusFilter } from "./useStatusFilter"
import { useDataSourceFilter } from "./useDataSourceFilter"
import { downloadFile } from "src/services/api/document"
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined"
import { useDataSourcesSelection } from "./useDataSourcesSelection"

interface DataSource {
  id: string
  fileName: string
  status: "ADDED" | "PROCESSING" | "FAILED"
  uploadedBy: string
  approvedBy: string
  uploadDate: string
}

type SortField = "file_name" | "status" | "upload_user_name" | "approver_name" | "file_upload_date"

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: "6px",
  backgroundColor: theme.palette.common.white,
  border: `1px solid #E0E0E0`,
  height: "auto",
  "&:hover": {
    backgroundColor: theme.palette.common.white,
    color: "#898989",
    boxShadow: `1px 1px 6px #898989`
  }
}))

const getStatusBackgroundColor = (status: string, primaryLightColor: string): string => {
  const upperStatus = status.toUpperCase()
  switch (upperStatus) {
    case "IN PROGRESS":
      return "#F5F5F5"
    case "ERROR":
    case "DELETED":
      return "#FFEBEE"
    case "PENDING":
      return "#FFD974"
    default:
      return primaryLightColor
  }
}

const DataSourcesTable = () => {
  const { snackbarShowMessage } = useSnackbar()
  const { showLoader, hideLoader } = useGlobalLoader()
  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const { searchQuery, debouncedSearchQuery, setSearchQuery } = useSearchQuery()
  const { statusFilter, setStatusFilter, statusOptions, getStatus } = useStatusFilter({
    storageKey: "dataSourcesStatusFilter"
  })
  const { dataSourceFilter, setDataSourceFilter, dataSourceOptions, canModify } = useDataSourceFilter()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [order, setOrder] = useState<Order>("desc")
  const [orderBy, setOrderBy] = useState<SortField>("file_upload_date")
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  const { t } = useTranslation("common")
  const { logger } = useLogger()
  const { palette } = useTheme()
  const { isMobile } = useResponsive()

  const fetchData = async (ignoreLoader = false) => {
    if (!dataSourceFilter) return // Don't fetch if no data source is selected

    if (!ignoreLoader) {
      showLoader()
    }
    try {
      const searchParam = debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ""
      const statusParam = statusFilter
        ?.flatMap((status) => status.value)
        .map((status) => `&status=${status}`)
        .join("")
      const offset = page * rowsPerPage
      const sortParam = `&_sort=${orderBy}&_order=${order.toUpperCase()}`
      const response = await axios.get(
        `/api/km/ingest/documents?communityId=${dataSourceFilter.value}${statusParam}${searchParam}${sortParam}&_offset=${offset}&_limit=${rowsPerPage}`
      )
      setDataSources(
        response.data.map((item: any) => ({
          id: item.id,
          fileName: item.file_name,
          status: getStatus(item.status),
          uploadedBy: item.upload_user_name || "-",
          approvedBy: item.approver_name || (["PENDING", "UPLOADING"].includes(item.status) ? "PENDING" : "-"),
          uploadDate: moment(item.file_upload_date).format("MM/DD/YYYY, HH:mm")
        }))
      )
      const totalCountHeader = response.headers["x-total-count"]
      setTotalCount(totalCountHeader ? parseInt(totalCountHeader) : dataSources.length)
    } catch (e) {
      if (e instanceof RequestError && e.status !== 403) {
        snackbarErrorHandler(e, { snackbarShowMessage }, t)
      }
      logger.error("System - Failed to fetch data sources", e)
    } finally {
      if (!ignoreLoader) {
        hideLoader()
      }
    }
  }

  const {
    selectedIds,
    setSelectedIds,
    successMessage,
    handleSelectAll,
    handleSelectOne,
    clearSelection,
    isSelected,
    isAllSelected,
    isSomeSelected,
    hasDeletableRecords,
    allSelectedArePending,
    handleBulkDelete,
    handleBulkApprove
  } = useDataSourcesSelection({
    dataSources,
    dataSourceFilter,
    onDataRefresh: fetchData
  })

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    fetchData()
    setSelectedIds([]) // Clear selection when filters/pagination changes
  }, [dataSourceFilter, statusFilter, debouncedSearchQuery, page, rowsPerPage, order, orderBy])

  useInterval(() => fetchData(true), 10000)
  const handleDelete = (id: string) => {
    axios.delete(`/api/km/ingest/document/${id}`).then((res) => {
      snackbarShowMessage("Your source file has been successfully deleted.")
      fetchData()
    })
  }

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
    setPage(0)
  }

  return (
    <Stack
      sx={{
        width: { xs: "100vw", md: "auto" },
        flex: 1,
        minHeight: 0,
        overflow: "hidden"
      }}
    >
      <DataSourcesFilter
        searchQuery={searchQuery}
        onSearchQueryChange={(value) => {
          setSearchQuery(value)
          setPage(0)
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(statuses) => {
          setStatusFilter(statuses)
          setPage(0)
        }}
        statusOptions={statusOptions}
        dataSourceFilter={dataSourceFilter}
        onDataSourceFilterChange={(dataSource) => {
          setDataSourceFilter(dataSource)
          setPage(0)
        }}
        dataSourceOptions={dataSourceOptions}
        onRefresh={() => fetchData()}
        onUploadClick={() => setIsUploadModalOpen(true)}
      />

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          flex: 1,
          minHeight: 0,
          overflow: "auto"
        }}
      >
        <Table sx={{ minWidth: { md: 650 } }} aria-label="data sources table" size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                  checkedIcon={<CheckBoxOutlinedIcon />}
                />
              </TableCell>
              <SortableTableHeaderCell<SortField>
                label="File name"
                orderBy={orderBy}
                order={order}
                property="file_name"
                onRequestSort={handleRequestSort}
                leftPadding
              />
              <SortableTableHeaderCell<SortField>
                label="Status"
                orderBy={orderBy}
                order={order}
                property="status"
                onRequestSort={handleRequestSort}
              />
              <SortableTableHeaderCell<SortField>
                label="Uploaded by"
                orderBy={orderBy}
                order={order}
                property="upload_user_name"
                onRequestSort={handleRequestSort}
              />
              <SortableTableHeaderCell<SortField>
                label="Approved by"
                orderBy={orderBy}
                order={order}
                property="approver_name"
                onRequestSort={handleRequestSort}
              />
              <SortableTableHeaderCell<SortField>
                label="Upload date"
                orderBy={orderBy}
                order={order}
                property="file_upload_date"
                onRequestSort={handleRequestSort}
              />
              <TableCell>
                <TableHeaderText>Actions</TableHeaderText>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataSources.map((source) => (
              <TableRow key={source.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected(source.id)}
                    onChange={() => handleSelectOne(source.id)}
                    checkedIcon={<CheckBoxOutlinedIcon />}
                  />
                </TableCell>
                <TableCell sx={{ pl: { xs: 2, md: 6 } }}>{source.fileName}</TableCell>
                <TableCell>
                  <StatusChip
                    label={source.status.toUpperCase()}
                    backgroundColor={getStatusBackgroundColor(source.status, palette.primary.light)}
                  />
                </TableCell>
                <TableCell>{source.uploadedBy}</TableCell>
                <TableCell>{source.approvedBy}</TableCell>
                <TableCell>{source.uploadDate}</TableCell>
                <TableCell sx={{ py: 0 }}>
                  {source.status.toUpperCase() !== "DELETED" && canModify && (
                    <Stack direction="row" spacing={1}>
                      <StyledIconButton size="small" onClick={() => handleDownload(source.id, source.fileName)}>
                        <LaunchIcon />
                      </StyledIconButton>
                      <StyledIconButton size="small" onClick={() => handleDelete(source.id)}>
                        <DeleteIcon />
                      </StyledIconButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <UploadSourcesModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={() => fetchData()}
      />

      <DataSourcesSelectionBar
        selectedCount={selectedIds.length}
        successMessage={successMessage}
        onClose={clearSelection}
        onApprove={handleBulkApprove}
        onDelete={handleBulkDelete}
        canModify={canModify}
        allSelectedArePending={allSelectedArePending}
        hasDeletableRecords={hasDeletableRecords}
      />
    </Stack>
  )
}

export default DataSourcesTable

export const handleDownload = (id: string, fileName: string) => downloadFile(`/api/km/ingest/file/${id}`, fileName)
